import { action, computed, makeObservable, observable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Amount } from 'src_common/common/amount/Amount';
import { DateTime } from 'src_common/utils/time/time';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { GetAvailableBonusesForAccountSuccessResponseType } from 'src_server/types/accountBonuses';
import { FreeBetType } from './FreeBetSelector';

export type AvailableFreeBetType = {
    id: string;
    uid: string | null;
    type: string;
    selectionId: string | null;
    label: string;
    expiry: string;
    currentAmount: Amount;
    rawAmount: number;
};

export interface SelectionFreeBetsType {
    id: string | null;
    freeBets: string[];
}

export class FreeBetSelectorState {
    private readonly accountBonusesResource: Resource<GetAvailableBonusesForAccountSuccessResponseType | null>;

    @observable public isDropdownOpen: boolean = false;

    public constructor(
        private readonly bonusEngineFreeBets: Array<FreeBetType>,
        private readonly betType: string,
        private readonly userId: number,
        private readonly common: Common
    ) {
        makeObservable(this);

        this.accountBonusesResource = new Resource(
            async (): Promise<GetAvailableBonusesForAccountSuccessResponseType | null> => {
                try {
                    const response =
                        await this.common.trpcClient.client.accountBonuses.getAvailableBonusesForAccount.query({
                            userId: this.userId,
                        });

                    if (response.responseStatus === 'success') {
                        return response.data;
                    }

                    console.error(`Unexpected http response`, response);
                } catch (error) {
                    console.error('Error fetching free bets:', error);
                }
                return null;
            }
        );
    }

    @computed public get availableFreeBets(): AvailableFreeBetType[] {
        const config = ConfigState.get(this.common);
        const data = this.accountBonusesResource.getReady();

        if (data === null) {
            return [];
        }

        const accountFreeBets = data['free-bet'];
        const selectionFreeBetsEntry = this.bonusEngineFreeBets.filter((freeBet) => freeBet.type === this.betType)[0];

        if (accountFreeBets === undefined || selectionFreeBetsEntry === undefined) {
            return [];
        }

        return accountFreeBets
            .filter(({ bonusId }) => selectionFreeBetsEntry.freeBets.includes(bonusId))
            .map(({ expiryAt, bonusId, rewardName, currentAmount }) => {
                const expiryDate = DateTime.from(expiryAt);

                const expiresDateLabel = expiryDate === null ? 'N/A' : expiryDate.format('DD/MM/YYYY');
                const expiresTimeLabel = expiryDate === null ? 'N/A' : expiryDate.format('HH:mm');

                return {
                    selectionId: selectionFreeBetsEntry.id ?? null,
                    uid: selectionFreeBetsEntry.uid ?? null,
                    type: this.betType,
                    id: bonusId,
                    label: rewardName ?? 'Free Bet',
                    rawAmount: currentAmount ?? 0,
                    currentAmount: config.precision.newFromAnything(currentAmount ?? 0),
                    expiry: `Expires on ${expiresDateLabel} at ${expiresTimeLabel}`,
                };
            });
    }

    @action public toggleDropdown = async (): Promise<void> => {
        const newOpenValue = !this.isDropdownOpen;

        if (this.accountBonusesResource.getReady() !== null && newOpenValue === true) {
            await this.accountBonusesResource.refresh();
        }

        this.isDropdownOpen = newOpenValue;
    };
}
